import { Component, Input, OnInit, OnChanges, SimpleChanges, ViewChild, ElementRef, OnDestroy, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { Chart, ChartConfiguration, ChartType as ChartJSType } from 'src/app/utils/chart-config';
import { Hole, HoleStatus, HoleStatusHistoryUtil } from '../../../services/hole.service';

type ChartType = 'loading' | 'drilling';

interface StatusData {
  status: string;
  count: number;
  percentage: number;
  color: string;
}

@Component({
  selector: 'app-holes-status-chart',
  templateUrl: './holes-status-chart.component.html',
  styleUrls: ['./holes-status-chart.component.scss'],
  standalone: true,
  imports: [CommonModule, TranslateModule]
})
export class HolesStatusChartComponent implements OnChanges, OnDestroy, AfterViewInit {
  @Input() holes: any[] = [];
  @Input() typeChart: ChartType;
  @ViewChild('chartCanvas', { static: false }) chartCanvas!: ElementRef<HTMLCanvasElement>;
  
  private chart: Chart | undefined;
  statusData: StatusData[] = [];
  private viewInitialized = false;

  private readonly DRILLED_COLOR = '#8661AE';
  private readonly TO_DRILL_COLOR = '#D1C8DA';

  ngAfterViewInit() {
    this.viewInitialized = true;
    setTimeout(() => {
      this.updateChart();
    }, 0);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['holes'] && !changes['holes'].firstChange && this.viewInitialized) {
      this.updateChart();
    }
  }

  private calculateDrilledStatusData(): StatusData[] {
    if (!this.holes || this.holes.length === 0) {
      return [];
    }

    const drilledStatuses = new Set([
      HoleStatus.BLASTED,
      HoleStatus.LOADED,
      HoleStatus.LOADING,
      HoleStatus.TOADJUST,
      HoleStatus.REDRILLED,
      HoleStatus.TO_REDRILL,
      HoleStatus.DRILLED
    ]);

    let drilledCount = 0;
    let toDrillCount = 0;

    this.holes.forEach((hole) => {
      if (hole.statusHistory !== undefined && hole.statusHistory !== null) {
        const statusHistory = HoleStatusHistoryUtil.getStatusHistory(hole.statusHistory);
        const hasDrilledStatus = statusHistory.some(status => drilledStatuses.has(status));
        
        if (hasDrilledStatus) {
          drilledCount++;
        } else {
          toDrillCount++;
        }
      } else {
        const currentStatus = hole.Status;
        if (currentStatus && drilledStatuses.has(currentStatus as HoleStatus)) {
          drilledCount++;
        } else {
          toDrillCount++;
        }
      }
    });

    const totalHoles = this.holes.length;
    const result: StatusData[] = [];

    if (drilledCount > 0) {
      result.push({
        status: 'Drilled',
        count: drilledCount,
        percentage: Math.round((drilledCount / totalHoles) * 100 * 10) / 10,
        color: this.DRILLED_COLOR
      });
    }

    if (toDrillCount > 0) {
      result.push({
        status: 'To Drill',
        count: toDrillCount,
        percentage: Math.round((toDrillCount / totalHoles) * 100 * 10) / 10,
        color: this.TO_DRILL_COLOR
      });
    }

    return result;
  }

  private calculateLoadingStatusData(): StatusData[] {
    if (!this.holes || this.holes.length === 0) {
      return [];
    }

    const drilledStatuses = new Set([
      HoleStatus.LOADED,
      HoleStatus.LOADING,
    ]);

    let loadingCount = 0;
    let toLoadCount = 0;

    this.holes.forEach((hole) => {
      if (hole.statusHistory !== undefined && hole.statusHistory !== null) {
        const statusHistory = HoleStatusHistoryUtil.getStatusHistory(hole.statusHistory);
        const hasDrilledStatus = statusHistory.some(status => drilledStatuses.has(status));
        
        if (hasDrilledStatus) {
          loadingCount++;
        } else {
          toLoadCount++;
        }
      } else {
        const currentStatus = hole.Status;
        if (currentStatus && drilledStatuses.has(currentStatus as HoleStatus)) {
          loadingCount++;
        } else {
          toLoadCount++;
        }
      }
    });

    const totalHoles = this.holes.length;
    const result: StatusData[] = [];

    if (loadingCount > 0) {
      result.push({
        status: 'Loaded',
        count: loadingCount,
        percentage: Math.round((loadingCount / totalHoles) * 100 * 10) / 10,
        color: this.DRILLED_COLOR
      });
    }

    if (toLoadCount > 0) {
      result.push({
        status: 'ToLoad',
        count: toLoadCount,
        percentage: Math.round((toLoadCount / totalHoles) * 100 * 10) / 10,
        color: this.TO_DRILL_COLOR
      });
    }

    return result;
  }

  private updateChart() {
    if (!this.chartCanvas?.nativeElement) {
      return;
    }
    this.statusData = this.typeChart == 'drilling' ? this.calculateDrilledStatusData() : this.calculateLoadingStatusData();
    if (this.chart) {
      this.chart.destroy();
    }

    if (this.statusData.length === 0) {
      this.chartCanvas.nativeElement.style.display = 'none';
      return;
    }

    const chartWrapper = document.querySelector('.chart-wrapper');
    if (chartWrapper && this.chartCanvas.nativeElement.parentNode !== chartWrapper) {
      chartWrapper.appendChild(this.chartCanvas.nativeElement);
    }
    this.chartCanvas.nativeElement.style.display = 'block';

    const ctx = this.chartCanvas.nativeElement.getContext('2d');
    if (!ctx) return;

    this.chart = new Chart(ctx, {
      type: 'doughnut',
      data: {
        labels: this.statusData.map(item => `${item.status} (${item.percentage}%)`),
        datasets: [{
          data: this.statusData.map(item => item.count),
          backgroundColor: this.statusData.map(item => item.color),
          borderWidth: 2,
          borderColor: '#ffffff'
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            callbacks: {
              label: (tooltipItem) => {
                const dataset = tooltipItem.chart.data.datasets![tooltipItem.datasetIndex!];
                const value = dataset.data![tooltipItem.dataIndex!] as number;
                const statusItem = this.statusData[tooltipItem.dataIndex!];
                return `${statusItem.status}: ${value} holes (${statusItem.percentage}%)`;
              }
            }
          }
        }
      }
    });
  }

  getDrilledPercentage(): number {
    const drilledData = this.statusData.find(item => item.status === 'Drilled');
    return drilledData ? drilledData.percentage : 0;
  }

  getTotalHoles(): number {
    return this.holes ? this.holes.length : 0;
  }

  ngOnDestroy() {
    if (this.chart) {
      this.chart.destroy();
    }
  }
}
